import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { AboutDialogComponent } from '@core/components/about-dialog/about-dialog.component';
import { MenuTool } from '@core/components/pages-container/interfaces/MenuTool.interface';
import {
  NavigationItem,
  RoutePath,
} from '@core/components/pages-container/interfaces/navigationList.interface';
import { PageContainerConfigurations } from '@core/components/pages-container/interfaces/PageContainerConfiguration.interface';
import {
  FiltersLocation,
  FiltersRegion,
} from '@core/components/pages-container/interfaces/region.interface';
import { PagesContainerComponent } from '@core/components/pages-container/pages-container.component';
import { PageContainerService } from '@core/components/pages-container/services/page-container.service';
import { LocationsService, SettingPermissionsService } from '@core/services';
import { AppToolsService } from '@core/services/appTools.service';
import { PermissionsService } from '@core/services/permissions.service';
import { ProfileService } from '@core/services/profile.service';
import { MainDashboardsService } from '@feature/BI-dashboards/services/main-dashboards.service';
import { HOME_ROUTE } from '@shared/common/constants';
import { BISettingsPermissionsScreenCodes } from '@shared/common/permissionsList.enum';
import {
  BISettingRoutes,
  routes,
  settingRoutes,
} from '@shared/common/route.conatant';
import { Dashboard } from '@shared/interfaces/dashboard.interface';
import { checkIfItLocalRoute, getRoutePath } from '@shared/utilities';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    CommonModule,
    PagesContainerComponent,
    AboutDialogComponent,
  ],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainLayoutComponent {
  readonly #pageContainerService = inject(PageContainerService);
  readonly #appToolsService = inject(AppToolsService);
  readonly #locationsService = inject(LocationsService);
  readonly #permissionsService = inject(PermissionsService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #profileService = inject(ProfileService);
  readonly #router = inject(Router);
  readonly #mainDashboardsService = inject(MainDashboardsService);
  readonly #settingPermissionsService = inject(SettingPermissionsService);

  navigationList: NavigationItem[] = [];
  dashboardRoutes: NavigationItem[] = [];
  settingRoutes: NavigationItem[] = environment.isStandalonePortal
    ? [BISettingRoutes]
    : [settingRoutes, BISettingRoutes];
  routePath: RoutePath[] = [];
  configurations: PageContainerConfigurations = {
    homeButton: {
      title: 'Dashboard',
      routeUrl: '',
      iconClass: 'ph ph-house',
    },
    settingRoute: '/settings',
    logoUrl: 'assets/images/Logo.svg',
    profile: {
      profileImg: 'assets/images/Avatars.png',
      profileRoute: '/profile-settings',
      userName: 'User Name',
    },
    hasHeaderFilters: true,
    hideTools: environment.isStandalonePortal,
  };

  appTools = signal<MenuTool[]>([]);
  isStandalonePortal = signal(environment.isStandalonePortal);

  regions = computed(
    () => this.#locationsService.userRegions() as FiltersRegion[],
  );
  locations = computed(
    () => this.#locationsService.userLocations() as FiltersLocation[],
  );
  selectedRegions: FiltersRegion[] = [];
  selectedLocations: FiltersLocation[] = [];

  isAboutDialogVisible = this.#profileService.showAboutDialog;

  ngOnInit(): void {
    this.initializeNavigationList();
    this.#mainDashboardsService.userDashboards.subscribe(dashboards => {
      this.loadDashboards(dashboards);
      // do something with your resolved data ...
    });
    this.initializeRoutePath();
    this.updateRoutePath();
    const allTools$ = this.#appToolsService.getAllTools().subscribe(tools => {
      this.appTools.set(tools);
    });

    const regionsFilters$ = this.#locationsService.selectedRegions$.subscribe(
      regions => (this.selectedRegions = regions),
    );

    const locationsFilter$ =
      this.#locationsService.selectedLocations$.subscribe(
        locations => (this.selectedLocations = locations as FiltersLocation[]),
      );

    this.#destroyRef.onDestroy(() => {
      allTools$.unsubscribe();
      locationsFilter$.unsubscribe();
      regionsFilters$.unsubscribe();
    });
  }

  private loadDashboards(dashboards: Dashboard[]): void {
    const mappedDashboards = this.mapDashboardsToNavigationItems(dashboards);
    this.dashboardRoutes = mappedDashboards;
    this.#pageContainerService.setNavigationList([
      ...this.navigationList,
      ...this.dashboardRoutes,
      ...this.settingRoutes,
    ]);
  }

  private mapDashboardsToNavigationItems(
    dashboards: Dashboard[],
  ): NavigationItem[] {
    const currentUrl = this.#router.url;
    const dashboardsList: NavigationItem[] = [
      {
        id: 13,
        name: 'Dashboards',
        iconClass: 'ph ph-chart-pie-slice',
        isOutSideUrl: false,
        routeUrl: '', // Set to empty string to prevent routing
        children: [],
      },
    ];

    if (!dashboards || dashboards.length === 0) {
      this.#router.navigate(['/bi-settings/']);
      return dashboardsList;
    }

    dashboardsList[0].children = dashboards
      .map((dashboard, i) => ({
        id: i + 1,
        name: dashboard.title ?? 'Untitled Dashboard',
        iconClass: 'ph ph-cursor',
        isOutSideUrl: !checkIfItLocalRoute(dashboard.url ?? ''),
        routeUrl: getRoutePath(dashboard.url ?? ''),
        children: [],
      }))
      .sort((a, b) => Number(a.isOutSideUrl) - Number(b.isOutSideUrl));

    if (currentUrl === '/' || currentUrl === '') {
      this.#router.navigate([dashboardsList[0].children[0]?.routeUrl]);
    }

    return dashboardsList;
  }

  private initializeRoutePath(): void {
    // Dashboard routes are now loaded dynamically from the API response.
    this.routePath = [
      // You may add static non-dashboard routes here if needed
      {
        url: '/dashboards/labor-dashboard',
        routePath: [13, 1],
      },
      {
        url: '/bi-settings/users-and-permissions/users',
        routePath: [122, 1],
      },
      {
        url: '/bi-settings/users-and-permissions/groups-permissions',
        routePath: [122, 1],
      },
      {
        url: '/bi-settings/dashboards-settings',
        routePath: [122, 2],
      },
    ];

    this.#pageContainerService.setRoutePath(this.routePath);
  }

  updateRoutePath(): void {
    this.dashboardRoutes[0].children.forEach(item => {
      const routePathIndex = this.routePath.findIndex(
        x => x.url === item.routeUrl,
      );
      if (routePathIndex !== -1) {
        this.routePath[routePathIndex].routePath[1] = item.id as number;
      }
    });
    this.#pageContainerService.setRoutePath(this.routePath);
  }

  private initializeNavigationList(): void {
    const rawNavigationList: NavigationItem[] = this.isStandalonePortal()
      ? []
      : routes;
    this.navigationList = this.filterNavigationList(rawNavigationList);
    this.settingRoutes = this.filterAndUpdateSettingNavigationList(
      this.settingRoutes,
    );

    this.#pageContainerService.setNavigationList([
      ...this.navigationList,
      ...this.settingRoutes,
    ]);
  }

  private filterNavigationList(items: NavigationItem[]): NavigationItem[] {
    return items
      .map(item => {
        // Recursively process children
        const filteredChildren = this.filterNavigationList(item.children || []);

        // Check if the current item has permissions
        const parentHasPermission =
          item.permissions &&
          this.#permissionsService.hasAnyPermission(item.permissions);

        // Retain item if:
        // - It has its own permissions
        // - It has children with valid permissions (filteredChildren is not empty)
        if (parentHasPermission || filteredChildren.length > 0) {
          return {
            ...item,
            children: filteredChildren, // Include only filtered children
          };
        }

        // Exclude items without permissions or valid children
        return null;
      })
      .filter(item => item !== null); // Remove null items
  }

  private filterAndUpdateSettingNavigationList(
    items: NavigationItem[],
  ): NavigationItem[] {
    return items
      .map(item => {
        // Recursively process children
        const filteredChildren = this.filterAndUpdateSettingNavigationList(
          item.children || [],
        );

        this.updateSettingRoute(item);

        const currentSettingPermission = this.#settingPermissionsService
          .settingScreensPermissions()
          .find(
            x =>
              item.settingPermissions?.length === 1 &&
              x.screenCode ===
                BISettingsPermissionsScreenCodes[item.settingPermissions[0]],
          );
        // Check if the current item has setting permissions
        const parentHasSettingPermission =
          item.settingPermissions &&
          this.#settingPermissionsService.hasAnyPermission(
            item.settingPermissions,
          ) &&
          currentSettingPermission?.canView;

        // Retain item if:
        // - It has its own setting permissions
        // - It has children with valid setting permissions (filteredChildren is not empty)
        if (parentHasSettingPermission || filteredChildren.length > 0) {
          return {
            ...item,
            children: filteredChildren, // Include only filtered children
          };
        }

        // Exclude items without setting permissions or valid children
        return null;
      })
      .filter(item => item !== null); // Remove null items
  }

  updateSettingRoute(item: NavigationItem) {
    const settingScreensData =
      this.#settingPermissionsService.settingScreensPermissions();

    if (item.settingPermissions?.length === 1) {
      const currentSetting = settingScreensData.find(
        x =>
          x.screenCode ===
          BISettingsPermissionsScreenCodes[
            (item as any)
              .settingPermissions[0] as unknown as keyof typeof BISettingsPermissionsScreenCodes
          ],
      );

      item.name = currentSetting?.title ?? item.name;
    }
  }

  selectedLocationChange(selectedLocations: FiltersLocation[]): void {
    this.#locationsService.updateSelectedLocations(selectedLocations);
  }

  selectedRegionChange(selectedRegions: FiltersRegion[]): void {
    this.#locationsService.updateSelectedRegions(selectedRegions);
  }

  openMessagesDrawer(): void {
    const url = environment.localDomain + HOME_ROUTE + '?messages=true';
    window.open(url, '_self');
  }

  openNotificationsDrawer(): void {
    const url = environment.localDomain + HOME_ROUTE + '?notifications=true';
    window.open(url, '_self');
  }
}
